// LeagueSystem — weekly matchday resolution, season finalization,
// promotion/relegation and automatic next-season generation.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { FEATURES, isFeatureUnlocked } from '@/config/clubLevelConfig'
import {
  SEASON,
  DIVISIONS,
  LOWEST_DIVISION,
  HIGHEST_DIVISION,
  PLAYER_CLUB_ID,
  LeagueState,
  RivalClub,
  Fixture,
  TieResult,
  createSeason,
  generateRivals,
  computeStandings,
  getSeasonPrize,
} from '@/config/leagueConfig'
import { ROSTER_RULES, Player, getEffectiveRating, isAvailable } from '@/config/playersConfig'
import { getDifficultyMods } from '@/config/difficultyConfig'
import { getRegionMods } from '@/config/regionConfig'
import { PadelTeam, simulateMatch } from './MatchSimulator'
import { Rng, defaultRng, rollInt } from '../utils/rng'

const INDOOR_HOME_FORM_BONUS = 5
const PROMOTION_REPUTATION = 0.3
const RELEGATION_REPUTATION = -0.2
const TITLE_REPUTATION = 0.5
/** Fatigue a player picks up per league tie */
const TIE_FATIGUE = 15

export interface EnrollCheck {
  can: boolean
  reasons: string[]
}

export function canEnrollInLeague(): EnrollCheck {
  const store = useGameStore.getState()
  const reasons: string[] = []

  if (!isFeatureUnlocked(FEATURES.LEAGUE, store.clubLevel)) {
    reasons.push('Requiere Club Establecido (nivel 3)')
  }
  if (store.players.length < ROSTER_RULES.leagueMinPlayers) {
    reasons.push(`Necesitas ${ROSTER_RULES.leagueMinPlayers} jugadores en plantilla`)
  }
  if (store.league) {
    reasons.push('Ya estas inscrito en la liga')
  }
  return { can: reasons.length === 0, reasons }
}

/** Enroll the club in the lowest division. Matchday 1 plays next week. */
export function enrollInLeague(rng: Rng = defaultRng): boolean {
  const store = useGameStore.getState()
  if (!canEnrollInLeague().can) return false

  store.setLeague(createSeason(LOWEST_DIVISION, 1, store.week, rng))
  return true
}

export class LeagueSystem {
  private handler = () => this.processWeek()

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.WEEK_CHANGED, this.handler)
  }

  destroy() {
    EventBus.off(GameEvents.WEEK_CHANGED, this.handler)
  }

  private processWeek() {
    const store = useGameStore.getState()
    const league = store.league
    if (!league || league.phase === 'finished') return

    const matchday = store.week - league.seasonStartWeek
    if (matchday < 1) return

    if (matchday <= SEASON.matchdays) {
      this.resolveUpTo(matchday)
    } else {
      this.finalizeSeason()
    }
  }

  /** Resolve every unresolved fixture up to the given matchday (catch-up safe) */
  private resolveUpTo(matchday: number) {
    const store = useGameStore.getState()
    const league = store.league!

    const fixtures = league.fixtures.map(f => ({ ...f }))
    let changed = false

    for (const fixture of fixtures) {
      if (fixture.result || fixture.matchday > matchday) continue
      fixture.result = this.resolveTie(fixture, league)
      changed = true

      if (fixture.homeId === PLAYER_CLUB_ID || fixture.awayId === PLAYER_CLUB_ID) {
        this.applyPlayerTieOutcome(fixture, league)
      }
    }

    if (changed || league.phase === 'preseason') {
      store.setLeague({ ...league, fixtures, phase: 'regular' })
    }
  }

  /** A tie is best-of-3 pair matches between two clubs */
  private resolveTie(fixture: Fixture, league: LeagueState): TieResult {
    const homePairs = this.buildPairs(fixture.homeId, league, true)
    const awayPairs = this.buildPairs(fixture.awayId, league, false)

    // Forfeit: a side without pairs loses 0-2
    if (!homePairs) {
      return { homeMatchWins: 0, awayMatchWins: 2, winnerId: fixture.awayId }
    }
    if (!awayPairs) {
      return { homeMatchWins: 2, awayMatchWins: 0, winnerId: fixture.homeId }
    }

    let homeWins = 0
    let awayWins = 0

    const play = (home: PadelTeam, away: PadelTeam) => {
      const result = simulateMatch(home, away, { homeAdvantage: 0, rng: this.rng })
      if (result.winner === 0) homeWins++
      else awayWins++
    }

    play(homePairs[0], awayPairs[0])
    play(homePairs[1], awayPairs[1])
    if (homeWins === 1 && awayWins === 1) {
      // Decider: each club fields its strongest pair
      play(homePairs[0], awayPairs[0])
    }

    return {
      homeMatchWins: homeWins,
      awayMatchWins: awayWins,
      winnerId: homeWins > awayWins ? fixture.homeId : fixture.awayId,
    }
  }

  /**
   * Two pairs per club. The player's club uses its top 4 AVAILABLE roster
   * players (injured players can't be fielded); returns null (forfeit)
   * below the league minimum. AI clubs get synthetic pairs.
   */
  private buildPairs(clubId: string, league: LeagueState, isHome: boolean): [PadelTeam, PadelTeam] | null {
    const store = useGameStore.getState()

    if (clubId === PLAYER_CLUB_ID) {
      const available = store.players.filter(isAvailable)
      if (available.length < ROSTER_RULES.leagueMinPlayers) return null

      const sorted = [...available].sort((a, b) => getEffectiveRating(b) - getEffectiveRating(a))
      const hasIndoor = store.courts.some(c => c.type === 'indoor')
      const formBonus = isHome && hasIndoor ? INDOOR_HOME_FORM_BONUS : 0

      const pairOf = (a: Player, b: Player): PadelTeam => ({
        name: `${a.name.split(' ').pop()} / ${b.name.split(' ').pop()}`,
        rating: (a.level + b.level) / 2,
        form: Math.min(100, (a.form + b.form) / 2 + formBonus),
        isClubTeam: true,
      })

      return [pairOf(sorted[0], sorted[1]), pairOf(sorted[2], sorted[3])]
    }

    const rival = league.clubs.find(c => c.id === clubId)
    if (!rival) return null

    // The region makes the field tougher (capital/resort) or softer (pueblo) for
    // the player. Applied to all AI pairs, so AI-vs-AI ties cancel out (both
    // shift equally) and only the player's matches feel it. 0 = identity.
    const rivalBoost = getRegionMods().rivalStrengthDelta
    const syntheticPair = (jitter: number): PadelTeam => ({
      name: rival.name,
      rating: Math.max(1, Math.min(100, rival.rating + jitter + rivalBoost)),
      form: rival.form,
      isClubTeam: false,
    })

    return [syntheticPair(rollInt(0, 4, this.rng)), syntheticPair(rollInt(-4, 0, this.rng))]
  }

  /** Money, reputation and player stats for the club's tie */
  private applyPlayerTieOutcome(fixture: Fixture, league: LeagueState) {
    const store = useGameStore.getState()
    const result = fixture.result!
    const won = result.winnerId === PLAYER_CLUB_ID
    const def = DIVISIONS[league.division]
    const rivalId = fixture.homeId === PLAYER_CLUB_ID ? fixture.awayId : fixture.homeId
    const rivalName = league.clubs.find(c => c.id === rivalId)?.name ?? 'Rival'

    if (won) {
      store.addMoney(def.matchdayWinBonus, `Liga J${fixture.matchday}: victoria vs ${rivalName}`)
      store.addReputation(def.reputationPerWin)
    } else {
      store.addReputation(-0.01)
    }

    // Stats, form and fatigue for the four players who competed (the same
    // availability-filtered lineup buildPairs fielded)
    const lineup = store.players.filter(isAvailable)
      .sort((a, b) => getEffectiveRating(b) - getEffectiveRating(a))
      .slice(0, 4)
    for (const player of lineup) {
      store.updatePlayer(player.id, {
        matchesPlayed: player.matchesPlayed + 1,
        matchesWon: player.matchesWon + (won ? 1 : 0),
        form: Math.max(0, Math.min(100, player.form + (won ? 3 : -2))),
        fatigue: Math.min(100, (player.fatigue ?? 0) + TIE_FATIGUE),
      })
    }
  }

  private finalizeSeason() {
    const store = useGameStore.getState()
    const league = store.league!
    const standings = computeStandings(league, 'Mi Club')
    const position = standings.findIndex(r => r.clubId === PLAYER_CLUB_ID) + 1

    // Season prize by final position (scaled by difficulty)
    const prize = Math.round(getSeasonPrize(league.division, position) * getDifficultyMods().prizeMultiplier)
    if (prize > 0) {
      store.addMoney(prize, `Liga T${league.season}: ${position}º en ${DIVISIONS[league.division].name}`)
    }

    // Title, promotion, relegation
    let nextDivision = league.division
    const title = position === 1 && league.division === HIGHEST_DIVISION
    if (title) {
      store.addReputation(TITLE_REPUTATION)
    }
    let promoted = false
    let relegated = false
    if (position <= SEASON.promotedCount && league.division > HIGHEST_DIVISION) {
      nextDivision = league.division - 1
      promoted = true
      store.addReputation(PROMOTION_REPUTATION)
    } else if (position > SEASON.clubsPerDivision - SEASON.relegatedCount && league.division < LOWEST_DIVISION) {
      nextDivision = league.division + 1
      relegated = true
      store.addReputation(RELEGATION_REPUTATION)
    }

    store.addLeagueHistory({ season: league.season, division: league.division, position })

    EventBus.emit(GameEvents.LEAGUE_SEASON_ENDED, {
      season: league.season,
      division: league.division,
      divisionName: DIVISIONS[league.division].name,
      position,
      promoted,
      relegated,
      title,
    })

    // Next season: same rivals evolve if staying; fresh rivals if moving
    const rivals: RivalClub[] | undefined = nextDivision === league.division
      ? league.clubs.map(c => {
          const def = DIVISIONS[league.division]
          const evolved = c.rating + rollInt(-3, 3, this.rng)
          return {
            ...c,
            rating: Math.max(def.rivalRatingRange[0] - 5, Math.min(def.rivalRatingRange[1] + 5, evolved)),
            form: rollInt(40, 60, this.rng),
          }
        })
      : generateRivals(nextDivision, this.rng)

    store.setLeague(createSeason(nextDivision, league.season + 1, store.week, this.rng, rivals))
  }
}
